import React, { useState } from 'react';
import { paymentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Plan structure mirrors PricingPage.js — keep them in sync or Razorpay flags
// inconsistency during account review. Free=creator workflow, PRO=business ops.
const PLANS = [
  {
    key: 'FREE',
    name: 'FREE',
    price: '₹0',
    period: 'forever',
    color: '#6b7280',
    features: [
      '3 Brand Deals',
      '10 Content Posts',
      '20 Ideas / 20 Captions / 20 Trending',
      '5 AI generations / day (shared)',
      'Bio Page (with CK badge)',
      'Basic analytics',
    ],
    options: null,
  },
  {
    key: 'PRO',
    name: 'PRO',
    price: '₹199',
    period: '/month',
    annualLine: 'or ₹1,799/year — save ₹589',
    color: '#8b5cf6',
    badge: '🔥 Most Popular',
    features: [
      'Unlimited Deals, Posts, Ideas, Captions, Trending',
      '100 AI generations / day (shared)',
      '🧾 GST Invoices + TDS',
      '📄 Contracts + PDF export',
      '💸 Payment / TDS Tracker',
      '💰 Rate Card + 📁 Media Kit',
      '📧 Outreach Templates',
      '🤝 Collabs Tracker',
      '🔄 Repurpose (8 platforms)',
      'Bio Page (no badge, custom theme)',
      'Advanced Analytics & Reports',
      'Email reminders + Priority support',
    ],
    options: [
      { key: 'PRO_MONTHLY', cta: 'Pay ₹199 / month',                  description: 'PRO — Monthly Subscription' },
      { key: 'PRO_ANNUAL',  cta: 'Pay ₹1,799 / year — save ₹589',     description: 'PRO — Annual Subscription', highlight: true },
    ],
  },
];

// Dynamically load Razorpay checkout script
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function UpgradeModal({ feature, limit, onClose, onUpgraded }) {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(null);
  const [error,   setError]   = useState('');

  const handleUpgrade = async (planKey, description) => {
    setError('');
    if (user && user.emailVerified === false) {
      setError('Please verify your email first. Check your inbox for the link (or use Resend on the banner).');
      return;
    }
    setLoading(planKey);

    try {
      // 1. Load Razorpay script
      const ok = await loadRazorpay();
      if (!ok) {
        setError('Failed to load payment gateway. Please check your internet connection.');
        setLoading(null);
        return;
      }

      // 2. Create Razorpay subscription via backend (returns sub_id; amount/
      //    currency are derived from the Razorpay plan attached to it).
      const { data: sub } = await paymentAPI.createSubscription(planKey);

      // 3. Open Razorpay checkout popup with subscription_id
      const options = {
        key:             sub.keyId,
        subscription_id: sub.subscriptionId,
        name:            'CreatorKhata',
        description:     description || `${planKey} Subscription`,
        image:           '/logo192.png',
        prefill: {
          name:  user?.name  || '',
          email: user?.email || '',
        },
        theme: { color: '#8b5cf6' },

        handler: async (response) => {
          try {
            // 4. Verify signature + activate subscription on backend
            const { data } = await paymentAPI.verifySubscription({
              paymentId:      response.razorpay_payment_id,
              subscriptionId: response.razorpay_subscription_id,
              signature:      response.razorpay_signature,
            });

            // 5. Update local user state so limits lift immediately
            setUser(prev => ({ ...prev, plan: data.plan }));
            if (onUpgraded) onUpgraded(data.plan);
            onClose();
            alert(`🎉 Welcome to ${data.plan}! Your subscription is active — unlimited access starts now.`);
          } catch {
            setError('Payment received but verification failed. Email support@creatorkhata.com with your payment ID.');
            setLoading(null);
          }
        },

        modal: { ondismiss: () => setLoading(null) },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setError(`Payment failed: ${resp.error.description}`);
        setLoading(null);
      });
      rzp.open();

    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong. Please try again.');
      setLoading(null);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      zIndex: 1000, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '1rem'
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: '20px', width: '100%',
        maxWidth: '680px', padding: '2rem', maxHeight: '90vh', overflowY: 'auto'
      }}>

        {/* Header — context-aware: limit=0 means PRO-only feature, limit>0 means cap hit. */}
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🚀</div>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.4rem', fontWeight: 800, color: '#0a0a0f', marginBottom: '0.4rem' }}>
            Upgrade Your Plan
          </div>
          {feature && (limit === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              <strong>{feature}</strong> is a PRO feature.
              Upgrade to unlock this and every other business tool.
            </div>
          ) : limit > 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              You've used all <strong>{limit} {feature}</strong> on the Free plan.
              Upgrade to keep going — no limits, ever.
            </div>
          ) : null)}
        </div>

        {/* Plans */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {PLANS.map(plan => {
            const isCurrent = user?.plan === plan.key;
            return (
              <div key={plan.key} style={{
                border: `2px solid ${plan.color}33`,
                borderRadius: '16px', padding: '1.25rem',
                position: 'relative',
                background: plan.key === 'PRO' ? `linear-gradient(135deg, ${plan.color}08, white)` : 'white'
              }}>
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
                    background: plan.color, color: 'white', fontSize: '0.65rem',
                    fontWeight: 700, padding: '0.2rem 0.75rem', borderRadius: '999px',
                    whiteSpace: 'nowrap'
                  }}>{plan.badge}</div>
                )}

                <div style={{ fontWeight: 800, fontSize: '1rem', color: plan.color, marginBottom: '0.25rem' }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.2rem', marginBottom: plan.annualLine ? '0.25rem' : '1rem' }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.6rem', fontWeight: 800, color: '#0a0a0f' }}>{plan.price}</span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{plan.period}</span>
                </div>

                {plan.annualLine && (
                  <div style={{ fontSize: '0.72rem', color: '#475569', fontWeight: 600, marginBottom: '1rem' }}>
                    {plan.annualLine}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.25rem' }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', fontSize: '0.75rem', color: '#374151' }}>
                      <span style={{ color: plan.color, marginTop: '1px', flexShrink: 0 }}>✓</span> {f}
                    </div>
                  ))}
                </div>

                {plan.options ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {plan.options.map(opt => (
                      <button
                        key={opt.key}
                        disabled={loading === opt.key || isCurrent}
                        onClick={() => handleUpgrade(opt.key, opt.description)}
                        style={{
                          width: '100%', padding: '0.6rem', border: 'none', borderRadius: '10px',
                          fontSize: '0.78rem', fontWeight: 700,
                          cursor: isCurrent ? 'default' : 'pointer',
                          background: isCurrent ? '#f3f4f6'
                            : opt.highlight
                              ? `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)`
                              : 'white',
                          color: isCurrent ? '#9ca3af' : opt.highlight ? 'white' : plan.color,
                          boxShadow: !isCurrent && !opt.highlight ? `inset 0 0 0 1.5px ${plan.color}` : 'none',
                          transition: 'opacity 0.15s'
                        }}
                      >
                        {loading === opt.key ? 'Opening payment...' :
                         isCurrent ? '✓ Current Plan' : opt.cta}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    disabled
                    style={{
                      width: '100%', padding: '0.6rem', border: 'none', borderRadius: '10px',
                      fontSize: '0.8rem', fontWeight: 700,
                      cursor: 'default', background: '#f3f4f6', color: '#9ca3af',
                    }}
                  >
                    {isCurrent ? '✓ Current Plan' : 'Current Plan'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div style={{
            padding: '0.75rem', background: '#fef2f2', borderRadius: '10px',
            fontSize: '0.78rem', color: '#dc2626', marginBottom: '1rem'
          }}>⚠️ {error}</div>
        )}

        <button onClick={onClose} style={{
          width: '100%', padding: '0.7rem', border: '1px solid #e5e7eb',
          borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
          cursor: 'pointer', background: 'white', color: '#6b7280'
        }}>
          Maybe Later
        </button>

        <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.72rem', color: '#9ca3af' }}>
          🔒 Secure payments via Razorpay · Cancel anytime · 7-day refund on every payment
        </div>
      </div>
    </div>
  );
}
